import { useEffect, useRef, type ReactNode, type RefObject } from "react"
import cx from "clsx"
import styles from "./AdminDialog.module.css"

/**
 * A modal on the admin surface.
 *
 * Built on <dialog> so a real browser supplies the focus trap, the inert
 * backdrop and the top layer rather than this reimplementing them. jsdom has no
 * `showModal` (v30), so opening falls back to the `open` attribute — the dialog
 * is then non-modal, which is enough for the component tests to see it, while
 * the Playwright journeys exercise the modal path in a real browser.
 *
 * Escape is handled here rather than through the `cancel` event, which only
 * fires for a dialog opened modally.
 */
export function AdminDialog({
  open,
  onClose,
  labelledBy,
  initialFocus,
  className,
  children,
}: {
  open: boolean
  onClose: () => void
  /** The id of the heading that names the dialog. */
  labelledBy: string
  /** Takes focus when the dialog opens; otherwise the browser chooses. */
  initialFocus?: RefObject<HTMLElement | null>
  className?: string
  children: ReactNode
}) {
  const ref = useRef<HTMLDialogElement>(null)

  useEffect(() => {
    const dialog = ref.current
    if (!dialog) return
    if (open) {
      if (typeof dialog.showModal === "function") dialog.showModal()
      else dialog.setAttribute("open", "")
      initialFocus?.current?.focus()
    } else if (typeof dialog.close === "function") {
      dialog.close()
    } else {
      dialog.removeAttribute("open")
    }
  }, [open, initialFocus])

  return (
    <dialog
      ref={ref}
      className={cx(styles.dialog, className)}
      aria-labelledby={labelledBy}
      onCancel={(e) => {
        e.preventDefault()
        onClose()
      }}
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          e.preventDefault()
          onClose()
        }
      }}
    >
      <button
        type="button"
        className={styles.close}
        onClick={onClose}
        aria-label="Close"
      >
        <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
          <path
            d="M4 4l8 8M12 4l-8 8"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
      </button>
      {children}
    </dialog>
  )
}
